import React from "react";
import { File } from "../../../../models";
import trimEnd from "lodash/trimEnd";
import { getCursorRange, setSelectionRange } from "../../../../lib";
import { insertImage } from "./commands";
import { S_IMAGE } from "./shortcuts";
import UploadProgress from "./UploadProgress";

import Icon from "./Icon";
import b from "./images/photo-b.png";
import w from "./images/photo-w.png";
import { CommandPropsWithShortcut } from "./types";
import { useState, useEffect } from "react";
import { useCallback } from "react";

const styleFileInput = {
    display: "none",
};

const mimesAccepted = [
    ".gif",
    "image/gif",
    ".png",
    "image/png",
    ".jpeg",
    "image/jpeg",
    ".webp",
    "image/webp",
];

type Props = CommandPropsWithShortcut & {
    doc_id: number;
    setErrorMsg: (msgId: string) => void;
};

export default function Image(props: Props) {
    const { enabled, enableDarkTheme } = props;
    const [uploading, setUploading] = useState(false);
    const fileField = React.createRef<HTMLInputElement>();
    let savedSelectionRange: Range | null = null;

    const onClick = useCallback(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        savedSelectionRange = getCursorRange();
        if (fileField && fileField.current) {
            fileField.current.click();
        }
    }, [fileField]);

    useEffect(() => {
        if (S_IMAGE === props.shortcut) {
            onClick();
        }
    }, [onClick, props.shortcut]);

    if (uploading) {
        return <UploadProgress fill={enableDarkTheme ? "#000" : "#fff"} />;
    }

    const addImage = (path: string) => {
        if (savedSelectionRange) {
            setSelectionRange(savedSelectionRange);
            savedSelectionRange = null;
        }

        insertImage(path);
        setUploading(false);
    };

    const onFileInputChange = async () => {
        const ff = fileField;
        if (
            !ff ||
            !ff.current ||
            !ff.current.files ||
            ff.current.files.length === 0
        ) {
            return;
        }

        const file = ff.current.files[0];
        const { type, size } = file;
        if (type.startsWith("image/")) {
            const kbSize = size / 1000;
            const maxSize = 1000.0;
            if (kbSize <= maxSize) {
                setUploading(true);

                try {
                    const formData = new FormData();
                    formData.append("file", file);

                    const { doc_id } = props;
                    const {
                        data: { name },
                    } = await File.upload(formData, { doc_id });
                    let url = File.fileURL(name, { doc_id });
                    url = trimEnd(url, "/");
                    addImage(url);
                } catch (e) {}
            } else {
                return props.setErrorMsg("toolbar.maxImageSizeError");
            }
        }
    };

    return (
        <>
            <Icon
                src={enableDarkTheme ? b : w}
                onClick={enabled ? onClick : undefined}
            />

            <input
                ref={fileField}
                type="file"
                multiple={false}
                style={styleFileInput}
                accept={mimesAccepted.join(",")}
                onChange={onFileInputChange}
            />
        </>
    );
}
